import { useEffect, useMemo, useState } from 'react';
import {
  collection, onSnapshot, query, orderBy, doc, updateDoc, setDoc, addDoc,
} from 'firebase/firestore';
import { Check, X, Mail, CalendarOff, Plus, Trash2, BedDouble, Receipt, FileText, ClipboardList } from 'lucide-react';
import { db, sendGraceHavenEmail } from '@/lib/firebase';
import { useBusiness } from '@/lib/business';
import { useAuth, can } from '@/lib/auth';
import { ROOMS } from '@/data/accommodation';
import { formatPula, round2 } from '@/lib/money';
import { nextNumber } from '@/lib/numbering';
import { Modal } from '@/components/ui/Modal';
import type { StayRequest, StayStatus, BlockedRange, DocLineItem } from '@/lib/types';

const uid = () => Math.random().toString(36).slice(2, 9);

/** Readable room label for a stay: joins multi-room names, falls back to the legacy single field. */
function stayRoomsLabel(s: StayRequest): string {
  if (s.rooms && s.rooms.length) return s.rooms.map((r) => r.roomName).join(', ');
  return s.roomName || '';
}
const STATUS: Record<StayStatus, string> = {
  new: 'bg-gold/15 text-gold-deep',
  confirmed: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-espresso/10 text-espresso',
  cancelled: 'bg-blush-soft text-blush-deep',
};

const KIND_LABEL: Record<'invoice' | 'receipt' | 'quotation', string> = {
  invoice: 'Invoice',
  receipt: 'Receipt',
  quotation: 'Quotation',
};

export function Stays() {
  const biz = useBusiness();
  const { role } = useAuth();
  const canFinance = can(role, 'invoices');
  const [stays, setStays] = useState<StayRequest[]>([]);
  const [ranges, setRanges] = useState<BlockedRange[]>([]);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [notice, setNotice] = useState<{ title: string; body: string } | null>(null);
  // blocked-range form
  const [bRoom, setBRoom] = useState('');
  const [bFrom, setBFrom] = useState('');
  const [bTo, setBTo] = useState('');
  const [bNote, setBNote] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'stayRequests'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => setStays(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<StayRequest, 'id'>) }))), () => setStays([]));
    const unsub2 = onSnapshot(doc(db, 'availability', 'calendar'), (snap) => setRanges(snap.exists() ? ((snap.data().ranges as BlockedRange[]) || []) : []), () => setRanges([]));
    return () => { unsub(); unsub2(); };
  }, []);

  function flash(m: string) { setToast(m); setTimeout(() => setToast(''), 3500); }

  async function setStatus(s: StayRequest, status: StayStatus) {
    try {
      await updateDoc(doc(db, 'stayRequests', s.id), {
        status, updatedAt: new Date().toISOString(),
        ...(status === 'confirmed' ? { confirmedAt: new Date().toISOString() } : {}),
      });
      flash(`Marked ${status}.`);
    } catch (e) { flash(e instanceof Error ? e.message : 'Failed.'); }
  }

  async function confirmAndEmail(s: StayRequest) {
    setBusy(true);
    try {
      await updateDoc(doc(db, 'stayRequests', s.id), {
        status: 'confirmed', confirmedAt: new Date().toISOString(),
        confirmationEmailSentAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
      });
      const label = stayRoomsLabel(s);
      const roomsCount = s.rooms?.length ?? 1;
      const body =
        `Dear ${s.fullName.split(' ')[0]},\n\n` +
        `Good news — your stay at ${biz.name} is confirmed.\n\n` +
        `${label ? `${roomsCount > 1 ? 'Rooms' : 'Room'}: ${label}\n` : ''}Check-in: ${s.checkIn}\nCheck-out: ${s.checkOut}\nGuests: ${s.guests || 1}` +
        `${s.nights ? `\nNights: ${s.nights}` : ''}${s.totalPrice ? `\nTotal: ${formatPula(s.totalPrice)}` : ''}\n\n` +
        `To make any changes, reply to this email or call ${biz.phone}. We look forward to welcoming you.`;
      await sendGraceHavenEmail({ to: s.email, subject: `Your stay is confirmed — ${biz.name}`, body, relatedEntityType: 'stay', relatedEntityId: s.id });
      flash('Confirmed and emailed the guest.');
    } catch (e) { flash(e instanceof Error ? e.message : 'Could not send.'); } finally { setBusy(false); }
  }

  /** Turn a stay into a finance document (receipt / invoice / quotation). */
  async function makeFinanceDoc(s: StayRequest, kind: 'invoice' | 'receipt' | 'quotation') {
    setBusy(true);
    try {
      const now = new Date().toISOString();
      const nights = Math.max(1, Number(s.nights || 1));
      const number = await nextNumber(kind);
      // One line item per room (room rate × nights); fall back to the legacy single total.
      const items: DocLineItem[] = (s.rooms && s.rooms.length)
        ? s.rooms.map((r) => ({
            description: `${r.roomName} — ${nights} night${nights === 1 ? '' : 's'}`,
            qty: 1,
            unitPrice: round2(Number(r.price || 0) * nights),
          }))
        : [{
            description: `${s.roomName || 'Room'} — ${nights} night${nights === 1 ? '' : 's'}`,
            qty: 1,
            unitPrice: Number(s.totalPrice || 0),
          }];
      const total = round2(items.reduce((sum, it) => sum + it.qty * it.unitPrice, 0));
      const payload: Record<string, unknown> = {
        number, kind,
        clientName: s.fullName,
        items,
        subtotal: total, vatRate: 0, vat: 0, total,
        status: kind === 'receipt' ? 'paid' : 'draft',
        createdAt: now, updatedAt: now,
      };
      if (s.email) payload.clientEmail = s.email;
      await addDoc(collection(db, 'documents'), payload);
      const label = KIND_LABEL[kind];
      setNotice({
        title: `${label} ${number} created`,
        body: `${label} ${number} created — find it in Invoices & Receipts to email or download.`,
      });
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Could not create the document.');
    } finally {
      setBusy(false);
    }
  }

  async function saveRanges(next: BlockedRange[]) {
    await setDoc(doc(db, 'availability', 'calendar'), { ranges: next, updatedAt: new Date().toISOString() }, { merge: true });
  }
  async function addRange() {
    if (!bFrom || !bTo) return flash('Pick both dates.');
    if (bTo < bFrom) return flash('End date must be after start.');
    const room = ROOMS.find((r) => r.id === bRoom);
    // Firestore rejects `undefined` (especially inside an array) — only set present keys.
    const range: BlockedRange = { id: uid(), from: bFrom, to: bTo };
    if (bRoom) range.roomId = bRoom;
    if (room?.name) range.roomName = room.name;
    const note = bNote.trim();
    if (note) range.note = note;
    const next = [...ranges, range];
    try { await saveRanges(next); setBFrom(''); setBTo(''); setBNote(''); setBRoom(''); flash('Dates blocked.'); }
    catch (e) { flash(e instanceof Error ? e.message : 'Failed.'); }
  }
  async function removeRange(id: string) {
    try { await saveRanges(ranges.filter((r) => r.id !== id)); flash('Removed.'); }
    catch (e) { flash(e instanceof Error ? e.message : 'Failed.'); }
  }

  const sortedRanges = useMemo(() => [...ranges].sort((a, b) => a.from.localeCompare(b.from)), [ranges]);

  return (
    <div>
      <h1 className="font-display text-3xl text-espresso">Stays &amp; Availability</h1>
      <p className="mt-1 font-body text-sm text-muted">Confirm stay requests and block out unavailable dates.</p>

      {/* Requests */}
      <p className="eyebrow mt-8 mb-3">Stay requests</p>
      <div className="overflow-x-auto border border-line bg-paper">
        <table className="w-full min-w-[680px] text-left">
          <thead>
            <tr className="border-b border-line font-body text-[11px] uppercase tracking-wide text-muted">
              <th className="px-5 py-3">Guest</th><th className="px-5 py-3">Room</th><th className="px-5 py-3">Dates</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {stays.map((s) => (
              <tr key={s.id} className="border-b border-line last:border-0 hover:bg-cream/40">
                <td className="px-5 py-3.5"><p className="font-body text-sm font-semibold text-espresso">{s.fullName}</p><p className="font-body text-xs text-muted">{s.email}</p></td>
                <td className="px-5 py-3.5 font-body text-sm text-muted">{stayRoomsLabel(s) || 'Any'} · {s.guests || 1}p</td>
                <td className="px-5 py-3.5 font-body text-sm text-muted">{s.checkIn} → {s.checkOut}{s.nights ? ` · ${s.nights}n` : ''}{s.totalPrice ? ` · ${formatPula(s.totalPrice)}` : ''}</td>
                <td className="px-5 py-3.5"><span className={`inline-block rounded-none px-2.5 py-1 font-body text-[10px] font-bold uppercase tracking-wide ${STATUS[s.status]}`}>{s.status}</span></td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center justify-end gap-3 text-muted">
                    <button onClick={() => confirmAndEmail(s)} disabled={busy} title="Confirm & email" className="hover:text-emerald-600"><Mail size={16} /></button>
                    <button onClick={() => setStatus(s, 'completed')} title="Completed" className="hover:text-espresso"><Check size={16} /></button>
                    <button onClick={() => setStatus(s, 'cancelled')} title="Cancel" className="hover:text-blush-deep"><X size={16} /></button>
                    {canFinance && (
                      <>
                        <span className="h-4 w-px bg-line" aria-hidden />
                        <button onClick={() => makeFinanceDoc(s, 'receipt')} disabled={busy} title="Create receipt" className="hover:text-gold-deep disabled:opacity-30"><Receipt size={16} /></button>
                        <button onClick={() => makeFinanceDoc(s, 'invoice')} disabled={busy} title="Create invoice" className="hover:text-gold-deep disabled:opacity-30"><FileText size={16} /></button>
                        <button onClick={() => makeFinanceDoc(s, 'quotation')} disabled={busy} title="Create quotation" className="hover:text-gold-deep disabled:opacity-30"><ClipboardList size={16} /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {stays.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center font-body text-sm text-muted">No stay requests yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Blocked dates */}
      <p className="eyebrow mt-10 mb-3 flex items-center gap-2"><CalendarOff size={15} /> Block out dates</p>
      <div className="border border-line bg-paper p-6">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto_1fr_auto]">
          <select className="field" value={bRoom} onChange={(e) => setBRoom(e.target.value)}>
            <option value="">All rooms</option>
            {ROOMS.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <input type="date" className="field" value={bFrom} onChange={(e) => setBFrom(e.target.value)} />
          <input type="date" className="field" value={bTo} onChange={(e) => setBTo(e.target.value)} />
          <input className="field" placeholder="Note (optional)" value={bNote} onChange={(e) => setBNote(e.target.value)} />
          <button onClick={addRange} className="btn-primary !px-4 !py-2.5"><Plus size={15} /> Block</button>
        </div>

        <div className="mt-5 divide-y divide-line border-t border-line">
          {sortedRanges.map((r) => (
            <div key={r.id} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3 font-body text-sm">
                <BedDouble size={15} className="text-gold-deep" />
                <span className="text-espresso">{r.from} → {r.to}</span>
                <span className="text-muted">· {r.roomName || 'All rooms'}{r.note ? ` · ${r.note}` : ''}</span>
              </div>
              <button onClick={() => removeRange(r.id)} className="text-muted hover:text-blush-deep" aria-label="Remove"><Trash2 size={15} /></button>
            </div>
          ))}
          {sortedRanges.length === 0 && <p className="py-4 font-body text-sm text-muted">No blocked dates. The website shows all dates as available.</p>}
        </div>
      </div>

      {notice && (
        <Modal onClose={() => setNotice(null)}>
          <h2 className="font-display text-2xl text-espresso">{notice.title}</h2>
          <div className="rule-gold mt-3" />
          <p className="mt-4 font-body text-sm leading-relaxed text-muted">{notice.body}</p>
          <div className="mt-6 flex justify-end">
            <button onClick={() => setNotice(null)} className="btn-primary">Done</button>
          </div>
        </Modal>
      )}

      {toast && <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 bg-espresso px-5 py-3 font-body text-sm text-cream shadow-soft">{toast}</div>}
    </div>
  );
}
